import { create } from 'zustand'
import { camelCase } from 'lodash/string'

import apiCapabilities from '../services/capabilities'

export class CapabilitiesLoadError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'CapabilitiesLoadError'
    this.code = code
  }
}

function classifyError(err) {
  if (err.name === 'AbortError' || err.name === 'CanceledError')
    return 'TIMEOUT'
  if (err.response?.status === 401 || err.response?.status === 403)
    return 'UNAUTHORIZED'
  if (err.response?.status >= 500) return 'SERVER_ERROR'
  if (!err.response) return 'NETWORK'
  return 'UNKNOWN'
}

function featureEntry(features, key) {
  if (!features) return undefined
  return features[key] ?? features[camelCase(key)]
}

function limitValue(limits, key) {
  if (!limits) return undefined
  return limits[key] ?? limits[camelCase(key)]
}

const CAPABILITIES_TIMEOUT_MS = 15000

export const useCapabilitiesStore = create((set, get) => ({
  capabilities: null,
  loaded: false,
  loading: false,
  error: null,
  errorCode: null,
  ensureLoaded: async () => {
    const { loaded, loading } = get()
    if (loaded || loading) return
    set({ loading: true, error: null, errorCode: null })
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(
        () => controller.abort(),
        CAPABILITIES_TIMEOUT_MS,
      )
      const capabilities = await apiCapabilities.getCapabilities({
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      set({ capabilities, loaded: true, loading: false })
    } catch (err) {
      const code = classifyError(err)
      const loadError = new CapabilitiesLoadError(code, err.message)
      set({ loading: false, error: loadError, errorCode: code })
      window.dispatchEvent(
        new CustomEvent('capabilities:error', { detail: loadError }),
      )
      throw loadError
    }
  },
  reset: () =>
    set({
      capabilities: null,
      loaded: false,
      loading: false,
      error: null,
      errorCode: null,
    }),
  refresh: async () => {
    set({ loaded: false, error: null, errorCode: null })
    await get().ensureLoaded()
  },
  setRemaining: (featureKey, value) =>
    set((state) => {
      const caps = state.capabilities
      if (!caps) return {}
      const features = { ...(caps.features || {}) }
      const camelKey = camelCase(featureKey)
      const keyInUse =
        features[featureKey] !== undefined
          ? featureKey
          : features[camelKey] !== undefined
            ? camelKey
            : featureKey
      features[keyInUse] = { ...(features[keyInUse] || {}), remaining: value }
      return { capabilities: { ...caps, features } }
    }),
  hasPermission: (permission) => {
    const caps = get().capabilities
    if (!caps?.permissions?.includes(permission)) return false
    const feature = featureEntry(caps?.features, permission)
    if (feature?.state === 'hidden' || feature?.state === 'disabled')
      return false
    return true
  },
  hasAnyPermission: (permissions) => {
    const capabilities = get().capabilities?.permissions ?? []
    return permissions.some((permission) => capabilities.includes(permission))
  },
  isEnabled: (key) => {
    const feature = featureEntry(get().capabilities?.features, key)
    if (!feature) return true
    const state = feature.state
    return state === 'enabled' || state === undefined
  },
  isVisible: (key) => {
    const feature = featureEntry(get().capabilities?.features, key)
    return feature !== undefined && feature.state !== 'hidden'
  },
  getFeature: (key) => featureEntry(get().capabilities?.features, key),
  isFeatureDisabled: (key) => {
    const caps = get().capabilities
    if (!caps?.permissions?.includes(key)) return false
    const feature = featureEntry(caps?.features, key)
    return feature?.state === 'disabled'
  },
  getLimit: (key) => limitValue(get().capabilities?.limits, key),
  getUsed: (key) =>
    featureEntry(get().capabilities?.features, key)?.used ?? undefined,
  getRemaining: (key) => {
    const feature = featureEntry(get().capabilities?.features, key)
    if (feature?.remaining !== undefined) return feature.remaining
    const limit = limitValue(get().capabilities?.limits, key)
    if (limit === undefined || limit === -1) return undefined
    return limit
  },
  checkLimit: (key) => {
    const limit = limitValue(get().capabilities?.limits, key)
    const used = featureEntry(get().capabilities?.features, key)?.used
    if (
      limit === undefined ||
      limit === -1 ||
      used === undefined ||
      used === null
    )
      return true
    return used < limit
  },
  isPlan: (name) =>
    get().capabilities?.subscription?.planName?.toLowerCase() ===
    name.toLowerCase(),
  isActive: () => {
    const status = get().capabilities?.subscription?.status
    return status === 'active' || status === 'trialing'
  },
  isTrialing: () => get().capabilities?.subscription?.status === 'trialing',
  canGrant: (category, indices) => {
    const raw = get().capabilities?.permissionsRaw
    if (!raw) return false
    const actorIndices = raw[category]
    if (!actorIndices) return false
    return indices.every((i) => actorIndices.includes(i))
  },
  getAllPermissions: () => get().capabilities?.permissions ?? [],
}))

export default useCapabilitiesStore
